import { Head, Link, router } from "@inertiajs/react"
import { MessageSquare, Bug, Lightbulb, ChevronRight } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"

const submissionsTabs = [
  { label: "Form Submissions", href: "/app/admin/submissions" },
  { label: "Feedback Requests", href: "/app/admin/feedback_submissions" },
]
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"

const typeConfig = {
  bug: { label: "Bug", icon: Bug, className: "bg-red-50 text-red-700 border-red-200" },
  feature_request: {
    label: "Feature Request",
    icon: Lightbulb,
    className: "bg-blue-50 text-blue-700 border-blue-200",
  },
  other: {
    label: "Other",
    icon: MessageSquare,
    className: "bg-zinc-100 text-zinc-700 border-zinc-200",
  },
}

function TypeBadge({ type }) {
  const config = typeConfig[type] || typeConfig.other
  const Icon = config.icon
  return (
    <Badge variant="outline" className={`gap-1 ${config.className}`}>
      <Icon size={11} />
      {config.label}
    </Badge>
  )
}

const statusConfig = {
  submitted: { label: "Submitted", className: "bg-amber-50 text-amber-700 border-amber-200" },
  working_on: { label: "Working On", className: "bg-blue-50 text-blue-700 border-blue-200" },
  completed: { label: "Completed", className: "bg-green-50 text-green-700 border-green-200" },
}

const statusOrder = ["submitted", "working_on", "completed"]

function StatusBadge({ status, submissionId }) {
  const config = statusConfig[status] || statusConfig.submitted

  const cycleStatus = (e) => {
    e.stopPropagation()
    const currentIndex = statusOrder.indexOf(status)
    const nextStatus = statusOrder[(currentIndex + 1) % statusOrder.length]
    router.patch(`/app/admin/feedback_submissions/${submissionId}`, { status: nextStatus }, {
      preserveScroll: true,
    })
  }

  return (
    <Badge
      variant="outline"
      className={`gap-1 cursor-pointer hover:opacity-80 transition-opacity ${config.className}`}
      onClick={cycleStatus}
    >
      {config.label}
    </Badge>
  )
}

export default function FeedbackIndex({ currentUser, submissions, pagination }) {
  return (
    <AdminLayout currentUser={currentUser}>
      <Head title="Feedback — Admin" />

      <div className="space-y-6">
        <SectionTabs tabs={submissionsTabs} />

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Feedback</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {pagination?.totalCount ?? 0} submission{pagination?.totalCount !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {submissions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-zinc-400">
                <MessageSquare size={36} className="mb-3 opacity-40" />
                <p className="text-sm font-medium">No feedback yet</p>
                <p className="text-xs mt-1">Submissions will appear here</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Type</TableHead>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="hidden md:table-cell">Submitted by</TableHead>
                    <TableHead className="hidden lg:table-cell">Page</TableHead>
                    <TableHead className="hidden sm:table-cell w-[120px]">Date</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((s) => (
                    <TableRow
                      key={s.id}
                      className="cursor-pointer hover:bg-zinc-50"
                      onClick={() =>
                        (window.location.href = `/app/admin/feedback_submissions/${s.id}`)
                      }
                    >
                      <TableCell>
                        <TypeBadge type={s.feedbackType} />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={s.status} submissionId={s.id} />
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <p className="text-sm text-zinc-800 truncate">{s.description}</p>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <p className="text-sm text-zinc-700">{s.submittedBy.name}</p>
                        <p className="text-xs text-zinc-400">{s.submittedBy.email}</p>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell max-w-[200px]">
                        <p className="text-xs text-zinc-400 truncate">{s.pageTitle || s.pageUrl}</p>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-xs text-zinc-400 whitespace-nowrap">
                        {new Date(s.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </TableCell>
                      <TableCell>
                        <ChevronRight size={14} className="text-zinc-300" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              {pagination.currentPage > 1 && (
                <PaginationItem>
                  <PaginationPrevious
                    href={`/app/admin/feedback_submissions?page=${pagination.currentPage - 1}`}
                  />
                </PaginationItem>
              )}
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    href={`/app/admin/feedback_submissions?page=${page}`}
                    isActive={page === pagination.currentPage}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              {pagination.currentPage < pagination.totalPages && (
                <PaginationItem>
                  <PaginationNext
                    href={`/app/admin/feedback_submissions?page=${pagination.currentPage + 1}`}
                  />
                </PaginationItem>
              )}
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </AdminLayout>
  )
}
